import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import PicturePreview from "containers/UploadPictures/PicturePreview";
import { showModal } from "redux/actions/modalActions";
import { event } from "analytics";
import { useTranslate } from "hooks/translation-hooks";
import { Button, Toolbar, PictureActionBlock } from "./PicturesLoadedStyled";
import { PicturesContainer } from "globalStyles/Containers";
import InlineLoader from "components/InlineLoader";
import PrimaryAction from "./PrimaryAction";
import ImageBox from "./ImageBox";
import PlaceHolderBox from "./PlaceHolderBox";
import { shouldShowContinueModalSelector } from "redux/selectors/config";

const PicturesLoaded = ({
  maximum_number_of_pictures,
  isLoading,
  discardPicture,
  sendPictures,
  doneWithPictures,
  uploadPictures,
  pictures
}) => {
  const t = useTranslate();
  const [preview, setPreview] = useState({ show: false });
  const shouldShowContinueModal = useSelector(shouldShowContinueModalSelector);
  const onPictureClickHandler = ({ name, src, status }) =>
    setPreview({ src, name, status, show: true });
  const closePreview = () => setPreview({});
  const dispatch = useDispatch();
  const showMediaOptions = () => {
    const action = pictures.length ? "upload_more" : "upload_now";
    const category = pictures.length
      ? "photos_incident_uploaded"
      : "take_photos_incident";
    event(action, category);
    dispatch(showModal({ type: "continue" }));
  };

  const pictureLoadedButtons = () => (
    <Toolbar data-category="upload_photos_preview">
      <PrimaryAction
        pictures={pictures}
        sendPictures={sendPictures}
        doneWithPictures={doneWithPictures}
      />
    </Toolbar>
  );

  const uploadPicturesButtons = () => (
    <Toolbar data-category="upload_photos_preview">
      <Button
        onClick={shouldShowContinueModal ? showMediaOptions : doneWithPictures}
        data-testid="upload_photos_card-button_1"
        data-action="upload_more-button-click"
      >
        {t("upload_photos_card-button_1")}
      </Button>
    </Toolbar>
  );

  const renderPlaceholders = () => {
    const maximumNumberOfPictures =
      maximum_number_of_pictures > 0 ? maximum_number_of_pictures : 0;
    const placeHolderArray = Array(
      Math.max(
        maximumNumberOfPictures -
          pictures.filter(({ status }) => status !== "SENT").length,
        0
      )
    ).fill(1);

    return placeHolderArray.map((_, key) => (
      <PlaceHolderBox uploadPictures={uploadPictures} index={key} key={key} />
    ));
  };

  return (
    <>
      {<InlineLoader isLoading={isLoading} />}
      <PictureActionBlock isLoading={isLoading}>
        <PicturesContainer>
          {pictures.map(({ src, name, status, historic }) => (
            <ImageBox
              key={name || src}
              name={name}
              src={src}
              onPictureClickHandler={onPictureClickHandler}
              status={status}
              discardPicture={discardPicture}
              historic={historic}
            />
          ))}
          {renderPlaceholders()}
        </PicturesContainer>
        {pictures.filter(({ status }) => status !== "ERROR").length
          ? pictureLoadedButtons()
          : uploadPicturesButtons()}
      </PictureActionBlock>
      {preview.show && (
        <PicturePreview
          discardPicture={discardPicture}
          closePreview={closePreview}
          {...preview}
        />
      )}
    </>
  );
};

PicturesLoaded.propTypes = {
  maximum_number_of_pictures: PropTypes.number.isRequired,
  discardPicture: PropTypes.func.isRequired,
  sendPictures: PropTypes.func.isRequired,
  doneWithPictures: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};

export default PicturesLoaded;
